import { useState } from "react";
import type { AdminGigListItemResponse } from "../../../types/adminTypes";
import { formatDate, formatDateInGigTimeZone } from "../../../utils/DateUtils";
import { formatGigCompensation } from "../../../utils/GigCompensationUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";
import Button from "../../ui/Button/Button";
import AdminPillBadge from "../ui/AdminPillBadge";
import AdminGigDetailModal from "./AdminGigDetailModal";
import AdminTableShell from "../ui/AdminTableShell";
import AdminGigsFilters from "./AdminGigsFilters";
import AdminGigsTableRow from "./AdminGigsTableRow";
import { adminGigsColumns } from "./adminGigsColumns";

type AdminGigsTableProps = {
  gigs: AdminGigListItemResponse[];
  isLoading: boolean;
  contentType: "GIGS" | "BUSINESS_LISTINGS";
  search: string;
  poster: string;
  status: string;
  createdFromDate: string;
  createdToDate: string;
  startFromDate: string;
  startToDate: string;
  pageSize: number;
  page: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSearchChange: (value: string) => void;
  onPosterChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onCreatedFromDateChange: (value: string) => void;
  onCreatedToDateChange: (value: string) => void;
  onStartFromDateChange: (value: string) => void;
  onStartToDateChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onPageChange: (nextPage: number) => void;
  onRequestTotalCount: () => void;
  onContentTypeChange: (value: "GIGS" | "BUSINESS_LISTINGS") => void;
};

const AdminGigsTable = ({
  gigs,
  isLoading,
  contentType,
  search,
  poster,
  status,
  createdFromDate,
  createdToDate,
  startFromDate,
  startToDate,
  pageSize,
  page,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onSearchChange,
  onPosterChange,
  onStatusChange,
  onCreatedFromDateChange,
  onCreatedToDateChange,
  onStartFromDateChange,
  onStartToDateChange,
  onPageSizeChange,
  onPageChange,
  onRequestTotalCount,
  onContentTypeChange,
}: AdminGigsTableProps) => {
  const [selectedGig, setSelectedGig] = useState<AdminGigListItemResponse | null>(null);

  const statusTone: Record<AdminGigListItemResponse["status"], "neutral" | "success" | "warning" | "danger"> = {
    DRAFT: "neutral",
    PUBLISHED: "success",
    FILLED: "warning",
    CANCELLED: "danger",
  };

  return (
    <>
      <AdminTableShell
        columns={adminGigsColumns}
        filters={
          <AdminGigsFilters
            contentType={contentType}
            search={search}
            poster={poster}
            status={status}
            createdFromDate={createdFromDate}
            createdToDate={createdToDate}
            startFromDate={startFromDate}
            startToDate={startToDate}
            pageSize={pageSize}
            onSearchChange={onSearchChange}
            onPosterChange={onPosterChange}
            onStatusChange={onStatusChange}
            onCreatedFromDateChange={onCreatedFromDateChange}
            onCreatedToDateChange={onCreatedToDateChange}
            onStartFromDateChange={onStartFromDateChange}
            onStartToDateChange={onStartToDateChange}
            onPageSizeChange={onPageSizeChange}
            onRequestTotalCount={onRequestTotalCount}
            onContentTypeChange={onContentTypeChange}
          />
        }
        mobileCards={gigs.map((gig) => (
          <article key={gig.id} className="rounded-[1.5rem] border border-myteengig-border bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-myteengig-text-primary">{gig.title}</p>
                <p className="mt-1 text-sm text-myteengig-text-muted">{gig.category}</p>
                <p className="mt-1 text-xs text-myteengig-text-muted">
                  {gig.startTime ? `Starts ${formatDateInGigTimeZone(gig.startTime, gig.timeZone)}` : "No schedule set"}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <AdminPillBadge tone={statusTone[gig.status]}>{gig.status}</AdminPillBadge>
                {gig.blocked ? <AdminPillBadge tone="danger">Blocked</AdminPillBadge> : null}
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Poster</p>
                <p className="mt-1 text-sm font-medium text-myteengig-text-primary">
                  {[gig.posterFirstName, gig.posterLastName].filter(Boolean).join(" ") || "Unknown poster"}
                </p>
                <p className="mt-1 text-sm text-myteengig-text-muted">{gig.posterEmail ?? "No email"}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Location</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{formatCityStateZip(gig)}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Compensation</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{formatGigCompensation(gig.paymentAmountCents, gig.volunteer)}</p>
              </div>
              <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Created</p>
                <p className="mt-1 text-sm text-myteengig-text-primary">{formatDate(gig.createdAt)}</p>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => setSelectedGig(gig)}>
                View
              </Button>
            </div>
          </article>
        ))}
        rows={gigs.map((gig) => (
          <AdminGigsTableRow key={gig.id} gig={gig} onView={setSelectedGig} />
        ))}
        isLoading={isLoading}
        isEmpty={!isLoading && gigs.length === 0}
        emptyMessage="No gigs found for the current filters."
        page={page}
        pageSize={pageSize}
        resultsCount={resultsCount}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={onPageChange}
      />
      <AdminGigDetailModal gig={selectedGig} isOpen={selectedGig !== null} onClose={() => setSelectedGig(null)} />
    </>
  );
};

export default AdminGigsTable;
